/**
 * The audio team's manifest (public/audio/audio-manifest.json) and its loading tiers: platform-neutral (the web
 * engine factory is apps/client `src/audio-bridge/engine.ts`; a native shell loads the same manifest from its bundle).
 */
import type { AudioAsset } from '@hf/audio';
import { getPlatform } from '../platform';

/** Where the audio team's manifest lives (public/audio/audio-manifest.json, relative to the site base). */
export const AUDIO_MANIFEST_PATH = 'audio/audio-manifest.json';

const isAsset = (a: unknown): a is AudioAsset => {
  const x = a as Partial<AudioAsset> | null;
  return (
    !!x &&
    typeof x.id === 'string' &&
    typeof x.url === 'string' &&
    (x.bus === 'music' || x.bus === 'sfx' || x.bus === 'crowd' || x.bus === 'announcer')
  );
};

/** Fetch and validate the audio manifest; a missing/broken manifest yields [] (the synth bank still works). */
export const loadAudioManifest = async (
  fetcher: (url: string) => Promise<{ ok: boolean; json(): Promise<unknown> }> = (u) => fetch(u),
  base = getPlatform().config.baseUrl,
): Promise<AudioAsset[]> => {
  try {
    const res = await fetcher(base.replace(/\/+$/, '') + '/' + AUDIO_MANIFEST_PATH);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json) ? json.filter(isAsset) : [];
  } catch {
    return [];
  }
};

/**
 * Loading tiers, so boot does not pull ~6 MB of audio over the network while the title art and the render chunk
 * load: 0 = right away (what the title and the Versus splash say, the title theme, the few sfx files), 1 = when
 * idle after the title is up (the crowd, the match calls, the menu theme), 2 = after that (alternate takes, special
 * callouts, Football 101's coach, the results outro). The bridge pulls everything in as soon as a match, a lesson
 * or a drill is near (`engine.prefetch()` on Versus / match / Football 101 / Practice).
 */
export type AudioTier = 0 | 1 | 2;
const BOOT_LINES: ReadonlySet<string> = new Set([
  'title',
  'choose_your_head',
  'versus',
  'rookie',
  'bigtex',
  'zorg',
  'grandpa',
  'kickoff',
]);
export const audioTier = (a: AudioAsset): AudioTier => {
  switch (a.bus) {
    case 'music':
      return a.id === 'mus_title' ? 0 : a.id === 'mus_menu' ? 1 : 2;
    case 'sfx':
      return 0;
    case 'crowd':
      return 1;
    default:
      if (BOOT_LINES.has(a.id)) return 0;
      if (a.id.startsWith('tut_') || a.id.startsWith('ready_') || /_(\d|alt)$/.test(a.id)) return 2;
      return 1;
  }
};
